import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Hash, Globe, MessageCircle, TrendingUp, Loader } from 'lucide-react';
import { searchAPI } from '../../utils/api';
import { getAvatarUrl, debounce } from '../../utils/helpers';
import './Search.css';

export default function GlobalSearch({ onClose }) {
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState(null);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [activeTab, setTab]     = useState('all');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
    searchAPI.trending().then(({ data }) => setTrending(data)).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doSearch = useCallback(
    debounce(async (q, tab) => {
      if (!q.trim()) { setResults(null); setLoading(false); return; }
      setLoading(true);
      try {
        const { data } = await searchAPI.search(q, tab);
        setResults(data);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 350),
    []
  );

  const handleChange = (e) => {
    setQuery(e.target.value);
    setLoading(true);
    doSearch(e.target.value, activeTab);
  };

  const handleTabChange = (tab) => {
    setTab(tab);
    if (query.trim()) doSearch(query, tab);
  };

  const goTo = (path) => { navigate(path); onClose(); };
  const hasResults = results && (
    (results.people?.length || 0) + (results.posts?.length || 0) +
    (results.communities?.length || 0) + (results.messages?.length || 0)
  ) > 0;

  const TABS = [
    { key: 'all',         label: 'All' },
    { key: 'people',      label: 'People',      icon: User },
    { key: 'posts',       label: 'Posts',       icon: Globe },
    { key: 'communities', label: 'Communities', icon: Hash },
    { key: 'messages',    label: 'Messages',    icon: MessageCircle },
  ];

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-modal" onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="search-input-row">
          <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="search-input"
            placeholder="Search people, posts, communities..."
            value={query}
            onChange={handleChange}
          />
          {loading && <Loader size={16} className="animate-spin" style={{ color: 'var(--primary)' }} />}
          {query && !loading && (
            <button className="btn-icon" onClick={() => { setQuery(''); setResults(null); }}>
              <X size={16} />
            </button>
          )}
          <button className="search-esc-btn" onClick={onClose}>Esc</button>
        </div>

        {/* Tabs */}
        {query && (
          <div className="search-tabs">
            {TABS.map(t => (
              <button key={t.key}
                className={`search-tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => handleTabChange(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="search-body">
          {/* Empty state — show trending */}
          {!query && (
            <div>
              <div className="search-section-title">
                <TrendingUp size={14} /> Trending
              </div>
              {trending.map(item => (
                <div key={item.term} className="search-result-item"
                  onClick={() => { setQuery(item.term); doSearch(item.term, 'all'); }}>
                  <div className="search-result-icon trending-icon">
                    <TrendingUp size={16} />
                  </div>
                  <div>
                    <p className="search-result-name">{item.term}</p>
                    <p className="search-result-sub">{item.count.toLocaleString()} posts</p>
                  </div>
                </div>
              ))}
              <div className="search-hint">
                <Search size={13} /> Try searching for people, posts, or communities
              </div>
            </div>
          )}

          {/* No results */}
          {query && results && !hasResults && !loading && (
            <div className="empty-state" style={{ padding: 40 }}>
              <Search size={36} />
              <h3>No results for "{query}"</h3>
              <p>Try a different search term</p>
            </div>
          )}

          {/* People */}
          {results?.people?.length > 0 && (activeTab === 'all' || activeTab === 'people') && (
            <div>
              <div className="search-section-title"><User size={13} /> People</div>
              {results.people.map(person => (
                <div key={person._id} className="search-result-item"
                  onClick={() => goTo(`/profile/${person._id}`)}>
                  <div style={{ position: 'relative' }}>
                    <img src={getAvatarUrl(person)} alt="" className="avatar" style={{ width: 40, height: 40 }} />
                    {person.isOnline && <span className="status-dot online" style={{ position:'absolute', bottom:0, right:0 }} />}
                  </div>
                  <div>
                    <p className="search-result-name">{person.displayName}</p>
                    <p className="search-result-sub">@{person.username} {person.bio && `· ${person.bio.slice(0,50)}`}</p>
                  </div>
                  <button className="btn btn-secondary btn-sm" style={{ marginLeft:'auto', flexShrink:0 }}
                    onClick={e => { e.stopPropagation(); goTo(`/chat/${person._id}`); }}>
                    Message
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Posts */}
          {results?.posts?.length > 0 && (activeTab === 'all' || activeTab === 'posts') && (
            <div>
              <div className="search-section-title"><Globe size={13} /> Posts</div>
              {results.posts.map(post => (
                <div key={post._id} className="search-result-item post-result"
                  onClick={() => goTo('/feed')}>
                  <img src={getAvatarUrl(post.author)} alt="" className="avatar" style={{ width: 36, height: 36, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <p className="search-result-name">{post.author?.displayName}</p>
                    <p className="search-result-content">{post.content.slice(0, 120)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Communities */}
          {results?.communities?.length > 0 && (activeTab === 'all' || activeTab === 'communities') && (
            <div>
              <div className="search-section-title"><Hash size={13} /> Communities</div>
              {results.communities.map(comm => (
                <div key={comm._id} className="search-result-item"
                  onClick={() => goTo(`/communities/${comm._id}`)}>
                  <div className="search-comm-icon">
                    {comm.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="search-result-name">{comm.name}</p>
                    <p className="search-result-sub">{comm.memberCount?.toLocaleString()} members</p>
                  </div>
                  <button className="btn btn-primary btn-sm" style={{ marginLeft:'auto', flexShrink:0 }}>
                    Join
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Messages */}
          {results?.messages?.length > 0 && (activeTab === 'all' || activeTab === 'messages') && (
            <div>
              <div className="search-section-title"><MessageCircle size={13} /> Messages</div>
              {results.messages.map(msg => (
                <div key={msg._id} className="search-result-item"
                  onClick={() => goTo(`/chat/${msg.sender._id}`)}>
                  <img src={getAvatarUrl(msg.sender)} alt="" className="avatar" style={{ width: 36, height: 36, flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <p className="search-result-name">{msg.sender?.displayName}</p>
                    <p className="search-result-content">{msg.content.slice(0, 80)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
